import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  Chip,
  Box,
  Modal,
  MenuItem,
  Select,
} from "@mui/material";
import { getAccountReportTableService } from "../../services/accountService";
import { toast } from "react-toastify";
import { getAllAgentsService } from "../../services/agentService";
import { fetchUserService } from "../../services/authService";

function AccountTable() {
  const [accountHistory, setAccountHistory] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(""); // Default filter
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch account history with optional type filter
  const fetchAccountHistory = async () => {
    setLoading(true);
    const response = await getAccountReportTableService(
      startDate,
      endDate,
      page + 1,
      rowsPerPage,
      selectedFilter,
      selectedAgent,
      searchTerm // Send filter value to API
    );
    if (response) {
      setAccountHistory(response.history);
      setTotalPages(response.totalCount);
    }
    setLoading(false);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };
  const handleAgentChange = (event) => {
    setSelectedAgent(event.target.value);
  };

  const handleGenerateClick = async () => {
    setPage(0);
    fetchAccountHistory();
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle chip click and set filter
  const handleChipClick = async (filterValue) => {
    setSelectedFilter(filterValue);
    setLoading(true);
    const response = await getAccountReportTableService(
      startDate,
      endDate,
      page + 1,
      rowsPerPage,
      filterValue,
      selectedAgent,
      searchTerm // Send filter value to API
    );
    if (response) {
      setAccountHistory(response.history);
      setTotalPages(response.totalCount);
    }
    setLoading(false);
  };

  // Chip data and styles
  const chipData = [
    { label: "All", value: "" }, // No filter for all
    { label: "Account Opening", value: "1", color: "primary" },
    { label: "Loan Disbursed", value: "2", color: "secondary" },
    {
      label: "Loan Collection",
      value: "3",
      sx: { backgroundColor: "#4caf50", color: "#fff" },
    },
    {
      label: "Group Loan Collection",
      value: "4",
      sx: { backgroundColor: "#ff9800", color: "#fff" },
    },
    {
      label: "Pigmy Collection",
      value: "5",
      sx: { backgroundColor: "#009688", color: "#fff" },
    },
  ];
  const fetchAgents = async () => {
    try {
      const agents = await getAllAgentsService(0, 50);
      setAgents(agents?.agentsWithPhoneContacts);
    } catch (err) {
      toast.error(err.message);
    }
  };
  const getData = async () => {
    try {
      const getUser = await fetchUserService();
      console.log(getUser);
      setAgents((prevAgents) => [...prevAgents, ...getUser]); // Push to agents array
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };
  useEffect(() => {
    fetchAccountHistory();
  }, [page, rowsPerPage, selectedFilter]);

  useEffect(() => {
    fetchAgents();
    getData();
  }, []);

  return (
    <Paper elevation={3} sx={{ padding: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Account Details
      </Typography>

      {/* Chips Row for Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        {chipData.map((chip, index) => (
          <Chip
            key={index}
            label={chip.label}
            onClick={() => handleChipClick(chip.value)}
            color={chip.color || "default"}
            sx={{
              cursor: "pointer",
              ...(selectedFilter === chip.value
                ? { border: "2px solid #4caf50" }
                : {}),
              ...chip.sx,
            }}
          />
        ))}
      </Box>

      {/* Date Filter and Generate Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          m: 1,
        }}
      >
        <TextField
          type="date"
          size="small"
          value={startDate}
          onChange={handleStartDateChange}
          sx={{ background: "#fff", borderRadius: "8px" }}
        />
        <TextField
          type="date"
          size="small"
          value={endDate}
          onChange={handleEndDateChange}
          sx={{ background: "#fff", borderRadius: "8px", ml: 2 }}
        />
        <Select
          size="small"
          name="agent"
          value={selectedAgent}
          onChange={handleAgentChange}
        >
          {agents.map((agent) => (
            <MenuItem key={agent._id} value={agent._id}>
              {agent.userName}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          placeholder="Search by Code or Phn no"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ ml: 2, background: "#fff", borderRadius: "8px" }}
        />

        <Button
          variant="contained"
          size="small"
          onClick={handleGenerateClick}
          sx={{
            py: 0.5,
            bgcolor: "green",
            textTransform: "capitalize",
            fontSize: "16px",
            borderRadius: "8px",
            ml: 2,
          }}
        >
          {loading ? (
            <CircularProgress size={24} style={{ color: "#fff" }} />
          ) : (
            "Generate"
          )}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Type</strong>
              </TableCell>
              <TableCell>
                <strong>ID</strong>
              </TableCell>
              <TableCell>
                <strong>AG/AD</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Amount (₹)</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Image</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Date</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Previous Balance (₹)</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Net Balance (₹)</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accountHistory?.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row?.user?.userName || "-"}</TableCell>
                <TableCell
                  align="right"
                  style={{ color: row.amount < 0 ? "red" : "green" }}
                >
                  {row.amount < 0
                    ? `-₹${Math.abs(row.amount)}`
                    : `+₹${row.amount}`}
                </TableCell>
                <TableCell align="right">
                  {row.image ? (
                    <img
                      src={row.image}
                      alt="Preview"
                      style={{ width: 50, height: 50, cursor: "pointer" }}
                      onClick={() => handleImageClick(row.image)}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell align="center">
                  {new Date(row.createdAt).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </TableCell>
                <TableCell align="right">
                  ₹{row.previousBalance.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  ₹{row.netBalance.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalPages}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Modal for Image Preview */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
          }}
        >
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Large Preview"
              style={{ width: "400px", height: "400px" }}
            />
          )}
        </Box>
      </Modal>
    </Paper>
  );
}

export default AccountTable;
